import { Ref, ref, reactive, onUnmounted } from '@vue/composition-api';
import { DialogPlugin } from 'tdesign-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import ipc from '@/renderer/scripts/utils/ipc';
import tips from '@/renderer/scripts/utils/tips';
import { loopAsyncTask } from '@/renderer/scripts/utils/syncTask';
import { formatError } from '@/renderer/scripts/utils/errorCode/index';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';

type targetPath = {
  value: string;
};
type FailInfo = {
  list: unknown[];
  type: string;
};
type MoveItem = {
  name: string;
  type: string;
  path: string[];
};
type OriginInfo = MoveItem & {
  data: MoveItem;
};
type MoveRequestItem = {
  from: string;
  to: string;
  moveAuthority?: boolean;
  type?: string;
  id?: number;
  conflictResolutionStrategy?: string;
};
type Handler = {
  show: Ref<boolean>;
  methods: {
    setupFileSource: (moveData: Array<MoveItem>) => void;
    openSelectFolder: (moveData: MoveItem[]) => void;
    onMove: (path: targetPath) => void;
  };
};
export default function handler(spaceId: Ref<SpaceId>): Handler {
  const currPath = ref('');
  const show = ref(false);
  const failInfo = reactive<FailInfo>({
    list: [],
    type: 'move',
  });
  let originFileList: OriginInfo[] = [];
  let pathKeep: targetPath;
  let moveAuth: boolean | undefined;
  const methods = {
    openSelectFolder(moveData: MoveItem[]) {
      methods.setupFileSource(moveData);
      show.value = true;
    },
    setupFileSource(moveData: Array<MoveItem>) {
      currPath.value = moveData[0].path.slice(0, -1).join('/');
      originFileList = [];
      for (let index = 0; index < moveData.length; index++) {
        const element = moveData[index];
        originFileList.push({
          data: element,
          ...element,
        });
      }
    },
    async onMove(path: targetPath, conflictResolutionStrategy = 'ask') {
      pathKeep = path;
      try {
        if (originFileList.length > 1) {
          const moveList: Array<MoveRequestItem> = [];
          const originFile = originFileList[0];
          const originFolderName = [currPath.value, originFile.name].join('/');
          const targetPath = path.value ? `${path.value}/${originFile.name}` : `/${originFile.name}`; // 为保持和currPath为空时一致，后者加/
          if (originFolderName === targetPath) {
            tips.error(i18n_t('所选路径为原路径，请重新选择'));
            return;
          }
          for (let index = 0; index < originFileList.length; index++) {
            const element = originFileList[index];
            moveList.push({
              from: [currPath.value, element.name].join('/'),
              to: path.value ? `${path.value}/${element.name}` : `${element.name}`,
              type: element.type,
              conflictResolutionStrategy: 'rename',
            });
          }
          if (
            moveList.find(
              (item: MoveRequestItem) =>
                item.to.indexOf(item.from) === 0 && item.to.replace(item.from, '').indexOf('/') === 0,
            )
          ) {
            tips.error(i18n_t('目标文件夹不可为被操作文件夹，请重新选择'));
            return;
          }
          show.value = false;
          if (originFileList.some(item => item.type === 'dir')) {
            const deleteDialog = CustomDialog.confirm({
              header: i18n_t('提示'),
              body: i18n_t('是否保留文件夹原有的权限？'),
              theme: 'warning',
              confirmBtn: i18n_t('是'),
              cancelBtn: i18n_t('否'),
              closeBtn: false,
              closeOnEscKeydown: false,
              closeOnOverlayClick: false,
              onConfirm: async () => {
                if (deleteDialog.hide) {
                  deleteDialog.hide();
                }
                moveAuth = true;
                for (let index = 0; index < moveList.length; index++) {
                  const element = moveList[index];
                  if (element.type === 'dir') {
                    element.moveAuthority = true;
                  }
                  delete element.type;
                }
                await methods.batchMoveOrRenameMethods(moveList);
              },
              onCancel: async () => {
                if (deleteDialog.hide) {
                  deleteDialog.hide();
                }
                moveAuth = false;
                for (let index = 0; index < moveList.length; index++) {
                  const element = moveList[index];
                  if (element.type === 'dir') {
                    element.moveAuthority = false;
                  }
                  delete element.type;
                }
                await methods.batchMoveOrRenameMethods(moveList);
              },
            });
          } else {
            await methods.batchMoveOrRenameMethods(moveList);
          }
        } else {
          const originFile = originFileList[0];
          const originFolderName = [currPath.value, originFile.name].join('/');
          const targetPath = path.value ? `${path.value}/${originFile.name}` : `/${originFile.name}`; // 为保持和currPath为空时一致，后者加/
          if (originFolderName === targetPath) {
            tips.error(i18n_t('所选路径为原路径，请重新选择'));
            return;
          }
          if (
            targetPath.indexOf(originFolderName) === 0 &&
            targetPath.replace(originFolderName, '').indexOf('/') === 0
          ) {
            tips.error(i18n_t('目标文件夹不可为被操作文件夹，请重新选择'));
            return;
          }
          const opt = {
            from: originFolderName,
            conflictResolutionStrategy,
            spaceId: spaceId.value,
          };
          if (originFile.type === 'dir') {
            show.value = false;
            if (moveAuth === undefined) {
              const deleteDialog = CustomDialog.confirm({
                header: i18n_t('提示'),
                body: i18n_t('是否保留文件夹原有的权限？'),
                theme: 'warning',
                confirmBtn: i18n_t('是'),
                cancelBtn: i18n_t('否'),
                closeBtn: false,
                closeOnEscKeydown: false,
                closeOnOverlayClick: false,
                onConfirm: () => {
                  if (deleteDialog.hide) {
                    deleteDialog.hide();
                  }
                  moveAuth = true;
                  methods.moveDirectory({
                    dirPath: targetPath,
                    moveAuthority: moveAuth,
                    ...opt,
                  });
                },
                onCancel: () => {
                  if (deleteDialog.hide) {
                    deleteDialog.hide();
                  }
                  moveAuth = false;
                  methods.moveDirectory({
                    dirPath: targetPath,
                    moveAuthority: moveAuth,
                    ...opt,
                  });
                },
              });
            } else {
              methods.moveDirectory({
                dirPath: targetPath,
                moveAuthority: moveAuth,
                ...opt,
              });
            }
          } else {
            tips.loading(i18n_t('正在移动'));
            const { data } = await smh.commonAPI.renameOrMoveFile({
              filePath: targetPath,
              ...opt,
            });
            show.value = false;
            tips.success(i18n_t('移动成功'));
            const moveTargetPath = data?.path || [];
            ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH, moveTargetPath, 'move');
          }
        }
      } catch (error: any) {
        if (error.status === 409) {
          show.value = false;
          ipc.emit(ipc.RENDERER_EVENTS_MAP.CONFLICT_SHOW, originFileList[0].type, 'move');
        } else {
          tips.error(error);
        }
      }
    },
    async moveDirectory(params: SMH.RenameOrMoveDirectoryParams) {
      try {
        tips.loading(i18n_t('正在移动'));
        await smh.commonAPI.renameOrMoveDirectory(params);
        tips.success(i18n_t('移动成功'));
        moveAuth = undefined;
        ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH);
      } catch (error: any) {
        if (error.status === 409) {
          show.value = false;
          ipc.emit(ipc.RENDERER_EVENTS_MAP.CONFLICT_SHOW, originFileList[0].type, 'move');
        } else {
          tips.error(error);
        }
      }
    },
    async batchMoveOrRenameMethods(data: Array<MoveRequestItem>) {
      try {
        tips.loading(i18n_t('正在移动'), 0);
        const res = await smh.commonAPI.batchMoveOrRename(
          {
            spaceId: spaceId.value,
          },
          {
            body: data,
          },
        );
        if (res.status === 200) {
          tips.success(i18n_t('移动成功'));
          moveAuth = undefined;
          ipc.emit(ipc.RENDERER_EVENTS_MAP.ERR_LIST_CLEAR);
        } else if (res.status === 202) {
          const asyncRes = await loopAsyncTask(smh.commonAPI.asyncTaskQuery, res.data.taskId, spaceId.value);
          if (asyncRes.status === 200) {
            tips.success(i18n_t('移动成功'));
          } else if (asyncRes.status === 207) {
            methods.handleBatchResult(asyncRes.result);
          }
        } else {
          methods.handleBatchResult(res.data.result);
        }
        ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH);
      } catch (error: any) {
        if (error.toString() === 'Error: [SMH Warning]: Logout') {
          tips.error(i18n_t('登出导致移动失败，请登录后重试'));
        } else {
          tips.error(error as MsgError);
        }
      }
    },
    handleBatchResult(data: Array<MoveRequestItem>) {
      failInfo.list = [];
      data.forEach((item: any, index: number) => {
        if (item.status !== 200 && item.status !== 204) {
          failInfo.list.push({
            from: item.from.join('/'),
            to: item.to.join('/'),
            msg: `${formatError(item.code, true)}`,
            id: index,
            moveAuthority: item.moveAuthority,
          });
        }
      });
      ipc.emit(ipc.RENDERER_EVENTS_MAP.ERRINFO_SHOW, failInfo);
    },
    async onMoveRetry(data: Array<MoveRequestItem>) {
      if (data.length === 1) {
        try {
          await smh.commonAPI.renameOrMoveDirectory({
            dirPath: data[0].to,
            from: data[0].from,
            spaceId: spaceId.value,
            moveAuthority: data[0].moveAuthority || moveAuth,
          });
          tips.success(i18n_t('移动成功'));
          ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH);
          ipc.emit(ipc.RENDERER_EVENTS_MAP.ERR_LIST_DEL, data[0].id);
        } catch (error) {
          tips.error(error as MsgError);
        }
      } else {
        await methods.batchMoveOrRenameMethods(data);
      }
    },
  };
  const conflictMove = (way: string) => {
    methods.onMove(pathKeep, way);
  };
  const retryMove = (data: Array<MoveRequestItem>) => {
    methods.onMoveRetry(data);
  };
  ipc.on(ipc.RENDERER_EVENTS_MAP.CONFLICT_MOVE, conflictMove);
  ipc.on(ipc.RENDERER_EVENTS_MAP.RETRY_MOVE, retryMove);
  onUnmounted(() => {
    ipc.off(ipc.RENDERER_EVENTS_MAP.CONFLICT_MOVE, conflictMove);
    ipc.off(ipc.RENDERER_EVENTS_MAP.RETRY_MOVE, retryMove);
  });
  return {
    show,
    methods,
  };
}
